import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import StarRating from './StarRating';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { formatGBP } from '../utils/currency';
import { phoneService, type PhoneNumber, type Country } from '../services/phoneService';
import type { NotificationPreferences, Review, Location, Transaction, Balance, Item } from '../types';
import './UserProfile.css';

const UserProfile: React.FC = () => {
  const { user } = useAuth();
  const { getUserItems, getUserRentals, getUserNotifications, getUserReviews, getUserTransactions, uploadProfilePicture, getUserData, updateUserPhoneNumber } = useData();
  const [activeTab, setActiveTab] = useState<'profile' | 'reviews' | 'transactions' | 'preferences' | 'balance'>('profile');
  const [isOnVacation, setIsOnVacation] = useState(false);
  const [vacationEndDate, setVacationEndDate] = useState<Date | null>(null);
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences>({
    email: true,
    push: true,
    sms: true,
    newRequests: true,
    statusUpdates: true,
    returnReminders: true,
    marketing: false
  });
  const [location, setLocation] = useState<Location>({
    address: "",
    city: "",
    postcode: "",
    coordinates: { latitude: 0, longitude: 0 }
  });
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  
  // Profile editing state
  const [phoneNumber, setPhoneNumber] = useState<PhoneNumber | null>(null);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isEditingProfilePicture, setIsEditingProfilePicture] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [profilePictureError, setProfilePictureError] = useState<string | null>(null);
  
  // User data from database
  const [userData, setUserData] = useState<any>(null);
  
  // Real data state
  const [userItems, setUserItems] = useState<Item[]>([]);
  const [userRentals, setUserRentals] = useState<any[]>([]);
  const [userReviews, setUserReviews] = useState<Review[]>([]);
  const [userTransactions, setUserTransactions] = useState<Transaction[]>([]);
  const [userStats, setUserStats] = useState({
    itemsLent: 0,
    itemsBorrowed: 0,
    totalRatings: 0,
    rating: 0
  });
  const [loading, setLoading] = useState(true);

  // Real balance data calculated from transactions
  const [userBalance, setUserBalance] = useState<Balance>({
    gbp: 0,
    pendingCredits: 0,
    pendingDebits: 0,
    lastUpdated: new Date().toISOString()
  });

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        
        // Load user data from database (including profile picture)
        const dbUserData = await getUserData(user.id);
        setUserData(dbUserData);
        
        // Load phone number from database if it exists
        if (dbUserData?.phone_number) {
          const parsedPhone = phoneService.parsePhoneNumber(dbUserData.phone_number);
          if (parsedPhone) {
            // Update with verification status from database
            parsedPhone.verified = dbUserData.phone_verified || false;
            setPhoneNumber(parsedPhone);
            
            // Set the selected country for editing
            const country = phoneService.getCountryByDialCode(parsedPhone.countryCode);
            if (country) {
              setSelectedCountry(country);
            }
          }
        }
        
        // Load user's items (items they're lending)
        const items = await getUserItems(user.id);
        setUserItems(items);
        
        // Load user's rentals (items they've borrowed)
        const rentals = await getUserRentals(user.id);
        setUserRentals(rentals);
        
        // Load user's notifications (which might contain reviews)
        const notifications = await getUserNotifications(user.id);
        
        // Load user's reviews
        const reviews = await getUserReviews(user.id);
        setUserReviews(reviews);
        
        // Load user's transactions
        const transactions = await getUserTransactions(user.id);
        setUserTransactions(transactions);
        
        // Calculate statistics
        const itemsLent = items.length;
        const itemsBorrowed = rentals.length;
        
        // Calculate average rating
        const totalRatings = reviews.length;
        const rating = totalRatings > 0 
          ? reviews.reduce((sum, review) => sum + review.rating, 0) / totalRatings 
          : 0;
        
        console.log('Profile Statistics (Real Data):', {
          itemsLent,
          itemsBorrowed,
          totalRatings,
          rating,
          dataSource: 'Database'
        });
        
        setUserStats({
          itemsLent,
          itemsBorrowed,
          totalRatings,
          rating
        });
        
        // Calculate balance based on transactions - only money earned from lending items
        const receivedTransactions = transactions.filter(t => t.type === 'received');
        const totalEarned = receivedTransactions.reduce((sum, t) => sum + t.amount, 0);
        
        // Calculate pending earnings (received transactions that are not completed)
        const pendingReceivedTransactions = receivedTransactions.filter(t => t.status !== 'completed');
        const pendingCredits = pendingReceivedTransactions.reduce((sum, t) => sum + t.amount, 0);
        
        setUserBalance({
          gbp: totalEarned, // Total earned from lending items
          pendingCredits, // Pending earnings
          pendingDebits: 0, // No pending debits since we only track earnings
          lastUpdated: new Date().toISOString()
        });
        
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user?.id, getUserItems, getUserRentals, getUserNotifications, getUserReviews, getUserTransactions, getUserData]);

  if (!user) return null;

  const handleNotificationChange = (key: keyof NotificationPreferences) => {
    setNotificationPrefs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleVacationToggle = () => {
    setIsOnVacation(prev => !prev);
    if (!isOnVacation) {
      setVacationEndDate(new Date());
    } else {
      setVacationEndDate(null);
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLocation(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePostcode = (postcode: string): boolean => {
    // Basic UK postcode validation
    const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;
    return postcodeRegex.test(postcode);
  };

  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddressError(null);

    if (!location.postcode || !validatePostcode(location.postcode)) {
      setAddressError("Please enter a valid UK postcode");
      return;
    }

    try {
      // TODO: Replace with actual geocoding API call
      // For now, using mock coordinates
      const updatedLocation: Location = {
        ...location,
        coordinates: {
          latitude: 52.2053,
          longitude: 0.1218
        }
      };

      // TODO: API call to update user's location
      setLocation(updatedLocation);
      setIsEditingAddress(false);
    } catch (error) {
      setAddressError("Failed to update address. Please try again.");
    }
  };

  const handleWithdraw = (e: React.FormEvent) => {
    e.preventDefault();
    setWithdrawError(null);

    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      setWithdrawError("Please enter a valid amount");
      return;
    }

    if (!withdrawAddress) {
      setWithdrawError("Please enter a wallet address");
      return;
    }

    // TODO: Implement withdrawal API call
    // For now, just close the modal
    setShowWithdrawModal(false);
    setWithdrawAmount('');
    setWithdrawAddress('');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumberInput(e.target.value);
    setPhoneError(null);
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    const country = phoneService.getCountryByCode(countryCode);
    setSelectedCountry(country || null);
    setPhoneError(null);
  };

  const handleSendVerificationCode = async () => {
    setPhoneError(null);
    
    if (!selectedCountry) {
      setPhoneError("Please select a country");
      return;
    }

    if (!phoneNumberInput.trim()) {
      setPhoneError("Please enter a phone number");
      return;
    }

    const phoneNumber = phoneService.createPhoneNumber(selectedCountry, phoneNumberInput);
    if (!phoneNumber) {
      setPhoneError("Please enter a valid phone number");
      return;
    }

    try {
      setIsSendingCode(true);
      const verificationId = await phoneService.sendVerificationCode(phoneNumber);
      setVerificationId(verificationId);
      setPhoneNumber(phoneNumber);
      setIsVerifying(true);
    } catch (error) {
      setPhoneError("Failed to send verification code. Please try again.");
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationId || !verificationCode) {
      setPhoneError("Please enter the verification code");
      return;
    }

    try {
      const isValid = await phoneService.verifyCode(verificationId, verificationCode);
      
      if (isValid && phoneNumber) {
        // Mark phone number as verified
        const verifiedPhone = { ...phoneNumber, verified: true };
        setPhoneNumber(verifiedPhone);
        
        // Save verified phone number to database
        await updateUserPhoneNumber(user.id, verifiedPhone);
        
        setIsEditingPhone(false);
        setIsVerifying(false);
        setVerificationCode('');
        setVerificationId(null);
        setPhoneNumberInput('');
        setSelectedCountry(null);
      } else {
        setPhoneError("Invalid verification code. Please try again.");
      }
    } catch (error) {
      setPhoneError("Failed to verify code. Please try again.");
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneError(null);

    if (!selectedCountry) {
      setPhoneError("Please select a country");
      return;
    }

    if (!phoneNumberInput.trim()) {
      setPhoneError("Please enter a phone number");
      return;
    }

    const phoneNumber = phoneService.createPhoneNumber(selectedCountry, phoneNumberInput);
    if (!phoneNumber) {
      setPhoneError("Please enter a valid phone number");
      return;
    }

    // Start SMS verification process
    await handleSendVerificationCode();
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setProfilePictureError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setProfilePictureError('Image file size must be less than 5MB');
        return;
      }

      setProfilePictureFile(file);
      setProfilePictureError(null);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfilePictureSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfilePictureError(null);

    if (!profilePictureFile) {
      setProfilePictureError('Please select an image file');
      return;
    }

    if (!user?.id) {
      setProfilePictureError('User not authenticated');
      return;
    }

    try {
      // Upload the profile picture
      const uploadedUrl = await uploadProfilePicture(user.id, profilePictureFile);
      
      if (uploadedUrl) {
        // Success - close modal and reset state
        setIsEditingProfilePicture(false);
        setProfilePictureFile(null);
        setProfilePicturePreview(null);
        
        // Refresh user data to show the new profile picture
        const updatedUserData = await getUserData(user.id);
        setUserData(updatedUserData);
        
        // Force a re-render of components that might be using user data
        // This will update the header profile picture as well
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: { userId: user.id } }));
        }, 100);
      } else {
        setProfilePictureError('Failed to upload profile picture. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      setProfilePictureError('Failed to upload profile picture. Please try again.');
    }
  };

  return (
    <div className="user-profile">
      <div className="profile-header">
        <div className="profile-avatar-container">
          <img 
            src={userData?.picture || user.user_metadata?.picture || '/default-avatar.svg'} 
            alt={userData?.name || user.user_metadata?.name || 'User'} 
            className="profile-avatar" 
          />
          <button 
            className="edit-avatar-button"
            onClick={() => setIsEditingProfilePicture(true)}
            title="Edit profile picture"
          >
            📷
          </button>
        </div>
        <div className="profile-info">
          <h1>{userData?.name || user.user_metadata?.name || user.email}</h1>
          <div className="profile-stats">
            <StarRating 
              rating={userStats.rating} 
              totalRatings={userStats.totalRatings}
              size="large"
            />
            <span className="joined-date">
              Member since {new Date(user.created_at || Date.now()).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      <div className="profile-summary">
        <div className="summary-stat">
          <span className="stat-value">{userStats.itemsLent}</span>
          <span className="stat-label">Items Lent</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{userStats.itemsBorrowed}</span>
          <span className="stat-label">Items Borrowed</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{userStats.totalRatings}</span>
          <span className="stat-label">Reviews</span>
        </div>
      </div>

      <div className="profile-tabs">
        <button 
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        <button 
          className={`tab-button ${activeTab === 'balance' ? 'active' : ''}`}
          onClick={() => setActiveTab('balance')}
        >
          Balance
        </button>
        <button 
          className={`tab-button ${activeTab === 'reviews' ? 'active' : ''}`}
          onClick={() => setActiveTab('reviews')}
        >
          Reviews
        </button>
        <button 
          className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          Transactions
        </button>
        <button 
          className={`tab-button ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          Preferences
        </button>
      </div>

      <div className="profile-content">
        {activeTab === 'profile' && (
          <div className="profile-section">
            <h2>About Me</h2>
            <p>Email: {user.email}</p>
            
            <div className="phone-section">
              <h3>Phone Number</h3>
              {!isEditingPhone ? (
                <div className="phone-display">
                  {phoneNumber ? (
                    <>
                      <div className="phone-info">
                        <p className="phone-number">
                          {phoneNumber.formatted}
                          {phoneNumber.verified && (
                            <span className="verified-badge">✓ Verified</span>
                          )}
                        </p>
                        <p className="phone-details">
                          Country: {phoneService.getCountryByDialCode(phoneNumber.countryCode)?.name || phoneNumber.countryCode}
                        </p>
                      </div>
                      <button 
                        className="edit-button"
                        onClick={() => setIsEditingPhone(true)}
                      >
                        Edit Phone
                      </button>
                    </>
                  ) : (
                    <button 
                      className="add-button"
                      onClick={() => setIsEditingPhone(true)}
                    >
                      Add Phone Number
                    </button>
                  )}
                </div>
              ) : (
                <div className="phone-form">
                  {!isVerifying ? (
                    <>
                      <div className="form-row">
                        <div className="form-group country-group">
                          <label htmlFor="country">Country</label>
                          <select
                            id="country"
                            value={selectedCountry?.code || ''}
                            onChange={handleCountryChange}
                            required
                          >
                            <option value="">Select a country</option>
                            {phoneService.getSupportedCountries().map((country: Country) => (
                              <option key={country.code} value={country.code}>
                                {country.flag} {country.name} ({country.dialCode})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group number-group">
                          <label htmlFor="phone">Phone Number</label>
                          <input
                            type="tel"
                            id="phone"
                            value={phoneNumberInput}
                            onChange={handlePhoneChange}
                            placeholder={selectedCountry ? `e.g., ${selectedCountry.dialCode === '+44' ? '7123 456789' : selectedCountry.dialCode === '+1' ? '555 123 4567' : '123 456 789'}` : "Enter phone number"}
                            required
                          />
                        </div>
                      </div>
                      {phoneError && (
                        <div className="error-message">{phoneError}</div>
                      )}
                      <div className="form-buttons">
                        <button 
                          type="button" 
                          className="save-button"
                          onClick={handlePhoneSubmit}
                          disabled={isSendingCode}
                        >
                          {isSendingCode ? 'Sending Code...' : 'Send Verification Code'}
                        </button>
                        <button 
                          type="button" 
                          className="cancel-button"
                          onClick={() => {
                            setIsEditingPhone(false);
                            setPhoneNumberInput('');
                            setPhoneError(null);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="verification-section">
                        <h4>Verify Your Phone Number</h4>
                        <p>We've sent a verification code to {phoneNumber?.formatted}</p>
                        
                        <div className="form-group">
                          <label htmlFor="verification-code">Verification Code</label>
                          <input
                            type="text"
                            id="verification-code"
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            placeholder="Enter 6-digit code"
                            maxLength={6}
                            required
                          />
                          <small>Enter the 6-digit code sent to your phone</small>
                        </div>
                        
                        {phoneError && (
                          <div className="error-message">{phoneError}</div>
                        )}
                        
                        <div className="form-buttons">
                          <button 
                            type="button" 
                            className="save-button"
                            onClick={handleVerifyCode}
                          >
                            Verify Code
                          </button>
                          <button 
                            type="button" 
                            className="cancel-button"
                            onClick={() => {
                              setIsVerifying(false);
                              setVerificationCode('');
                              setVerificationId(null);
                              setPhoneError(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="address-section">
              <h3>Address Details</h3>
              {!isEditingAddress ? (
                <div className="address-display">
                  {location.address ? (
                    <>
                      <p>{location.address}</p>
                      <p>{location.city}</p>
                      <p>{location.postcode}</p>
                      <button 
                        className="edit-button"
                        onClick={() => setIsEditingAddress(true)}
                      >
                        Edit Address
                      </button>
                    </>
                  ) : (
                    <button 
                      className="add-button"
                      onClick={() => setIsEditingAddress(true)}
                    >
                      Add Address
                    </button>
                  )}
                </div>
              ) : (
                <form onSubmit={handleAddressSubmit} className="address-form">
                  <div className="form-group">
                    <label htmlFor="address">Street Address</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={location.address || ''}
                      onChange={handleAddressChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={location.city || ''}
                      onChange={handleAddressChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="postcode">Postcode</label>
                    <input
                      type="text"
                      id="postcode"
                      name="postcode"
                      value={location.postcode || ''}
                      onChange={handleAddressChange}
                      required
                    />
                  </div>
                  {addressError && (
                    <div className="error-message">{addressError}</div>
                  )}
                  <div className="form-buttons">
                    <button type="submit" className="save-button">
                      Save Address
                    </button>
                    <button 
                      type="button" 
                      className="cancel-button"
                      onClick={() => setIsEditingAddress(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="vacation-mode">
              <h3>Vacation Mode</h3>
              <div className="vacation-toggle">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={isOnVacation}
                    onChange={handleVacationToggle}
                  />
                  <span className="slider"></span>
                </label>
                <span>I'm currently unavailable for lending</span>
              </div>
              {isOnVacation && (
                <div className="vacation-dates">
                  <label>Available again after:</label>
                  <DatePicker
                    selected={vacationEndDate}
                    onChange={(date: Date | null) => setVacationEndDate(date)}
                    minDate={new Date()}
                    placeholderText="Select end date"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'balance' && (
          <div className="balance-section">
            <h2>Your Earnings</h2>
            <div className="balance-cards">
              <div className="balance-card fiat">
                <h3>Total Earnings</h3>
                <div className="balance-items">
                  <div className="balance-item">
                    <div className="balance-info">
                      <span className="currency-icon">£</span>
                      <span className="currency-name">British Pound</span>
                    </div>
                    <span className="balance-amount">
                      {formatGBP(userBalance.gbp)}
                    </span>
                  </div>
                </div>
              </div>

              {userBalance.pendingCredits > 0 && (
                <div className="balance-card pending">
                  <h3>Pending Earnings</h3>
                  <div className="balance-items">
                    <div className="balance-item">
                      <span className="balance-label">Incoming</span>
                      <span className="balance-amount positive">
                        {formatGBP(userBalance.pendingCredits)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="balance-actions">
              <button 
                className="action-button withdraw"
                onClick={() => setShowWithdrawModal(true)}
                disabled={userBalance.gbp === 0}
              >
                {userBalance.gbp === 0 ? 'No Funds to Withdraw' : 'Withdraw to Debit Card'}
              </button>
            </div>

            <div className="balance-update">
              Last updated: {new Date(userBalance.lastUpdated).toLocaleString()}
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="reviews-section">
            <h2>Reviews ({userReviews.length})</h2>
            <div className="reviews-list">
              {userReviews.map(review => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <StarRating 
                      rating={review.rating} 
                      totalRatings={0}
                      size="small"
                      showCount={false}
                    />
                    <span className="reviewer-name">{review.reviewerName}</span>
                    <span className="review-date">
                      {new Date(review.date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="review-item">Item: {review.itemName}</p>
                  <p className="review-comment">{review.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="transactions-section">
            <h2>Transaction History</h2>
            <div className="transactions-list">
              {userTransactions.map(transaction => (
                <div key={transaction.id} className="transaction-card">
                  <div className="transaction-header">
                    <div className="transaction-type">
                      <span className={`transaction-icon ${transaction.type}`}>
                        {transaction.type === 'received' ? '↓' : '↑'}
                      </span>
                      <span className="transaction-method">
                        💳
                      </span>
                    </div>
                    <div className="transaction-amount">
                      <span className={`amount ${transaction.type}`}>
                        {transaction.type === 'received' ? '+' : '-'} {formatGBP(transaction.totalAmount)}
                      </span>
                    </div>
                  </div>
                  <div className="transaction-details">
                    <div className="transaction-item">
                      <span className="label">Item:</span>
                      <span className="value">{transaction.itemName}</span>
                    </div>
                    <div className="transaction-counterparty">
                      <span className="label">{transaction.type === 'received' ? 'From:' : 'To:'}</span>
                      <span className="value">{transaction.counterpartyName}</span>
                    </div>
                    <div className="transaction-date">
                      <span className="label">Date:</span>
                      <span className="value">{new Date(transaction.date).toLocaleDateString()}</span>
                    </div>
                    <div className="transaction-fee">
                      <span className="label">Service Fee:</span>
                      <span className="value">{formatGBP(transaction.serviceFee)}</span>
                    </div>
                  </div>
                  <div className="transaction-status">
                    <span className={`status-badge ${transaction.status}`}>
                      {transaction.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="preferences-section">
            <h2>Notification Preferences</h2>
            <div className="preferences-list">
              <div className="preference-item">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.email}
                    onChange={() => handleNotificationChange('email')}
                  />
                  <span className="slider"></span>
                </label>
                <div className="preference-label">
                  <span>Email Notifications</span>
                  <span className="preference-description">
                    Receive notifications via email
                  </span>
                </div>
              </div>
              <div className="preference-item">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.push}
                    onChange={() => handleNotificationChange('push')}
                  />
                  <span className="slider"></span>
                </label>
                <div className="preference-label">
                  <span>Push Notifications</span>
                  <span className="preference-description">
                    Receive push notifications
                  </span>
                </div>
              </div>
              <div className="preference-item">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.sms}
                    onChange={() => handleNotificationChange('sms')}
                  />
                  <span className="slider"></span>
                </label>
                <div className="preference-label">
                  <span>SMS Notifications</span>
                  <span className="preference-description">
                    Receive notifications via SMS
                  </span>
                </div>
              </div>
              <div className="preference-item">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.newRequests}
                    onChange={() => handleNotificationChange('newRequests')}
                  />
                  <span className="slider"></span>
                </label>
                <div className="preference-label">
                  <span>New Requests</span>
                  <span className="preference-description">
                    Get notified when someone wants to borrow your items
                  </span>
                </div>
              </div>
              <div className="preference-item">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.statusUpdates}
                    onChange={() => handleNotificationChange('statusUpdates')}
                  />
                  <span className="slider"></span>
                </label>
                <div className="preference-label">
                  <span>Status Updates</span>
                  <span className="preference-description">
                    Get notified about the status of your borrowing/lending
                  </span>
                </div>
              </div>
              <div className="preference-item">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.returnReminders}
                    onChange={() => handleNotificationChange('returnReminders')}
                  />
                  <span className="slider"></span>
                </label>
                <div className="preference-label">
                  <span>Return Reminders</span>
                  <span className="preference-description">
                    Get reminded when items are due to be returned
                  </span>
                </div>
              </div>
              <div className="preference-item">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.marketing}
                    onChange={() => handleNotificationChange('marketing')}
                  />
                  <span className="slider"></span>
                </label>
                <div className="preference-label">
                  <span>Marketing Updates</span>
                  <span className="preference-description">
                    Receive news and updates about Share My Shed
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Withdraw to Debit Card</h3>
            <form onSubmit={handleWithdraw}>
              <div className="form-group">
                <label htmlFor="amount">Amount (GBP)</label>
                <input
                  type="number"
                  id="amount"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Enter amount in GBP"
                  min="0.01"
                  max={userBalance.gbp / 100}
                  step="0.01"
                  required
                />
                <small>Available: {formatGBP(userBalance.gbp)}</small>
              </div>
              <div className="form-group">
                <label htmlFor="cardNumber">Card Number</label>
                <input
                  type="text"
                  id="cardNumber"
                  placeholder="1234 5678 9012 3456"
                  pattern="[0-9\s]{13,19}"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="expiryDate">Expiry Date</label>
                <input
                  type="text"
                  id="expiryDate"
                  placeholder="MM/YY"
                  pattern="[0-9]{2}/[0-9]{2}"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cvv">CVV</label>
                <input
                  type="text"
                  id="cvv"
                  placeholder="123"
                  pattern="[0-9]{3,4}"
                  required
                />
              </div>
              {withdrawError && (
                <div className="error-message">{withdrawError}</div>
              )}
              <div className="modal-actions">
                <button type="submit" className="action-button withdraw">
                  Withdraw to Card
                </button>
                <button 
                  type="button" 
                  className="action-button cancel"
                  onClick={() => {
                    setShowWithdrawModal(false);
                    setWithdrawAmount('');
                    setWithdrawAddress('');
                    setWithdrawError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profile Picture Modal */}
      {isEditingProfilePicture && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Update Profile Picture</h3>
            <form onSubmit={handleProfilePictureSubmit}>
              <div className="form-group">
                <label htmlFor="profilePicture">Select Image</label>
                <input
                  type="file"
                  id="profilePicture"
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                  required
                />
                <small>Max file size: 5MB. Supported formats: JPG, PNG, GIF</small>
              </div>
              
              {profilePicturePreview && (
                <div className="preview-section">
                  <h4>Preview:</h4>
                  <img 
                    src={profilePicturePreview} 
                    alt="Profile preview" 
                    className="profile-picture-preview"
                  />
                </div>
              )}
              
              {profilePictureError && (
                <div className="error-message">{profilePictureError}</div>
              )}
              
              <div className="modal-actions">
                <button type="submit" className="action-button save">
                  Upload Picture
                </button>
                <button 
                  type="button" 
                  className="action-button cancel"
                  onClick={() => {
                    setIsEditingProfilePicture(false);
                    setProfilePictureFile(null);
                    setProfilePicturePreview(null);
                    setProfilePictureError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile; 